import { useMemo, useState } from "react";
import { Card, message, Tooltip, Typography } from "antd";
import { MdOutlineInfo } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../../styles/Components/pricing-plan/individual-services/ServiceActivationBox.module.css";
import { getCustomRemainingTime } from "../../../utility/formatter-helper";
import useServiceStatus from "../../../hooks/useServiceStatus";
import useUserTier from "../../../hooks/useUserTier";
import { openSideMenu } from "../../../redux/ui/uiSlice";
import ServiceActivationMenu from "./ServiceActivationMenu";
import AlertServiceIcon from "../../../ui/icons/AlertServiceIcon";
import BotServiceIcon from "../../../ui/icons/BotServiceIcon";
import AiServiceIcon from "../../../ui/icons/AiServiceIcon";
import InsightsServiceIcon from "../../../ui/icons/InsightsServiceIcon";
import TradeSimulatorServiceIcon from "../../../ui/icons/TradeSimulatorServiceIcon";

const { Text, Title } = Typography;

const ServiceCard = ({
  service,
  cost,
  isActive,
  exDate,
  selected,
  onClick,
  icon,
}) => (
  <div style={{ cursor: isActive ? "not-allowed" : "pointer" }}>
    <Card
      className={`${styles.serviceCard} ${
        selected ? styles.selectedService : ""
      } ${isActive ? styles.activeService : ""}`}
      onClick={() => !isActive && onClick()}
      bordered={false}
    >
      <div className={styles.serviceContent}>
        <div className={styles.icon}>{icon}</div>
        <div className={styles.info}>
          <Title level={5}>{service.label}</Title>
          {!isActive ? (
            <Text className={styles.serviceCost}>${cost}/month</Text>
          ) : (
            <Tooltip
              title={`Active until: ${getCustomRemainingTime(exDate)}`}
              color="green"
            >
              <div className={styles.activeUntil}>
                <MdOutlineInfo className={styles.infoIcon} />
                <Text type="success" style={{ fontSize: "0.85rem" }}>
                  Active
                </Text>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </Card>
  </div>
);

function ServiceActivationBox() {
  const {
    pricingPlan: { pricingPlan },
  } = useSelector((state) => state);

  const dispatch = useDispatch();
  const [selectedServices, setSelectedServices] = useState([]);

  const { isActive: aiAssistantStatus, expirationDate: aiAssistantExDate } =
    useServiceStatus("aiAssistant");
  const { isActive: alertStatus, expirationDate: alertExDate } =
    useServiceStatus("alert");
  const { isActive: botStatus, expirationDate: botExDate } =
    useServiceStatus("bot");
  const { isActive: insightStatus, expirationDate: insightExDate } =
    useServiceStatus("insight");
  const {
    isActive: tradeSimulatorStatus,
    expirationDate: tradeSimulatorExDate,
  } = useServiceStatus("tradeSimulator");

  const { serviceType: aiKey } = useUserTier("myais");
  const { serviceType: alertKey } = useUserTier("alert");
  const { serviceType: botKey } = useUserTier("mybot");
  const { serviceType: insightKey } = useUserTier("insai");
  const { serviceType: tradeSimulatorKey } = useUserTier("trsim");

  const servicesCost = useMemo(() => {
    return {
      myais: pricingPlan[aiKey],
      alert: pricingPlan[alertKey],
      mybot: pricingPlan[botKey],
      insai: pricingPlan[insightKey],
      trsim: pricingPlan[tradeSimulatorKey],
    };
  }, [aiKey, alertKey, botKey, insightKey, pricingPlan, tradeSimulatorKey]);

  const services = useMemo(() => {
    return [
      {
        label: "AI Assistant",
        key: "myais",
        active: aiAssistantStatus,
        exDate: aiAssistantExDate,
        icon: <AiServiceIcon />,
      },
      {
        label: "Alert System",
        key: "alert",
        active: alertStatus,
        exDate: alertExDate,
        icon: <AlertServiceIcon />,
      },
      {
        label: "Bot",
        key: "mybot",
        active: botStatus,
        exDate: botExDate,
        icon: <BotServiceIcon />,
      },
      {
        label: "Insights",
        key: "insai",
        active: insightStatus,
        exDate: insightExDate,
        icon: <InsightsServiceIcon />,
      },
      {
        label: "Trade Simulator",
        key: "trsim",
        active: tradeSimulatorStatus,
        exDate: tradeSimulatorExDate,
        icon: <TradeSimulatorServiceIcon />,
      },
    ];
  }, [
    aiAssistantExDate,
    aiAssistantStatus,
    alertExDate,
    alertStatus,
    botExDate,
    botStatus,
    insightExDate,
    insightStatus,
    tradeSimulatorExDate,
    tradeSimulatorStatus,
  ]);

  const toggleService = (serviceKey) => {
    setSelectedServices((prev) =>
      prev.includes(serviceKey)
        ? prev.filter((key) => key !== serviceKey)
        : [...prev, serviceKey],
    );
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.section}>
            <h4 className={styles.title}>Available Services</h4>
            {/*<Title level={4}>Available Services</Title>*/}
            <div className={styles.servicesGrid}>
              {services.map((service) => (
                <ServiceCard
                  key={service.key}
                  service={service}
                  cost={servicesCost[service.key]}
                  isActive={service.active}
                  exDate={service.exDate}
                  selected={selectedServices.includes(service.key)}
                  icon={service?.icon}
                  onClick={() => toggleService(service.key)}
                />
              ))}
            </div>
          </div>

          <ServiceActivationMenu
            selectedServices={selectedServices}
            setSelectedServices={setSelectedServices}
          />
        </div>
      </div>

      <div
        className={`${styles.submitBtn} ${!selectedServices.length ? styles.disabledBtn : ""}`}
        onClick={() => {
          if (!selectedServices?.length)
            return message.info("Please select a service first!");
          dispatch(openSideMenu("active-service", "active-service"));
        }}
      >
        Continue
      </div>
    </>
  );
}

export default ServiceActivationBox;
